import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Section, Box, Flex, TextField, Text, Button, Table } from '@radix-ui/themes';
import { useFormik } from 'formik';

import PageTitle from 'components/layout/PageTitle';
import Editor from 'components/editor';

import { instance } from 'api/axios.instance';
import { selectType } from 'store/slices/users';
import useRenderComponent from 'hooks/useRenderComponent';
import { isValidProjectYear } from './projectYear';

// 지역 이슈 관리 > 신규 등록 목록 > 상세
export default function ProjectRequestDetail() {
  // hooks
  const navigate = useNavigate();
  const { id } = useParams();
  const type = useSelector(selectType); // 사용자 타입
  const { Components, handleRenderComponents } = useRenderComponent(); // 동적 컴포넌트 hook

  // state
  const [formValues, setFormValues] = useState({
    year: '', // 분류 연도
    title: '', // 프로젝트 명
    // alias: '', // 설명
    source: '', // 사업유형,
    reference: '', // 출처,
    thumbnail: null, // 썸네일
    agendaData: [] // 아젠다
  });

  // form 유효성 검사
  const formik = useFormik({
    initialValues: formValues,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true
  });

  // 프로젝트 상세 조회 API
  const fetchAgendaById = useCallback(async () => {
    const response = await instance(`/project/${id}`);

    if (response.data.status === 200) {
      handleRenderComponents(response.data.data.data.map(data => data.componentName));
      setFormValues({
        year: response.data.data.year ?? '',
        title: response.data.data.title,
        description: response.data.data.description || '',
        source: response.data.data.source || '',
        reference: response.data.data.reference || '',
        // alias: response.data.data.alias,
        stage: `${response.data.data.stage.split('단계')[0]}단계`,
        thumbnail: response.data.data.thumbnail?.fileUrl,
        agendaData: response.data.data.data.map(data => {
          return {
            templateCode: data.templateCode,
            templateData: data.templateData
          };
        })
      });
    }
  }, [handleRenderComponents, id]);

  // mount 시 호출
  useEffect(() => {
    fetchAgendaById();
  }, [fetchAgendaById]);

  return (
    <Section className="template-form-wrap">
      <PageTitle title="지역 이슈 프로젝트 상세" />

      <Box className="box box-white">
        <Box mb="var(--space-10)">
          <Table.Root className="table-write">
            <Table.Body>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>분류 연도</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <TextField.Root
                    readOnly
                    size="2"
                    id="year"
                    aria-label="분류 연도"
                    value={isValidProjectYear(formik.values.year) ? String(formik.values.year) : '-'}
                    style={{ maxWidth: '180px' }}
                  />
                </Table.Cell>
              </Table.Row>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>사업명</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Text as="label" htmlFor="title">
                    <TextField.Root
                      readOnly
                      size="2"
                      id="title"
                      required
                      placeholder="강원도 고령 인구로 인한 버스노선 증축"
                      value={formik.values.title}
                    />
                  </Text>
                </Table.Cell>
              </Table.Row>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>사업소개</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Text as="label" htmlFor="description">
                    <Box
                      style={{
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-2)',
                        minHeight: '150px',
                        padding: 'var(--space-2)'
                      }}
                    >
                      <Editor
                        editorData={formik.values.description}
                        setEditorData={() => {}} // 읽기 전용이므로 빈 함수
                        board="projectRequest"
                        readOnly={true}
                      />
                    </Box>
                  </Text>
                </Table.Cell>
              </Table.Row>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>사업유형</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Text as="label" htmlFor="source">
                    <TextField.Root
                      readOnly
                      size="2"
                      id="source"
                      required
                      defaultValue={formik.values.source}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Text>
                </Table.Cell>
              </Table.Row>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>출처</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Text as="label" htmlFor="reference">
                    <TextField.Root
                      readOnly
                      size="2"
                      id="reference"
                      required
                      defaultValue={formik.values.reference}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Text>
                </Table.Cell>
              </Table.Row>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>썸네일 첨부</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Flex direction="column" align="start" px="var(--space-1)">
                    {formik.values.thumbnail && (
                      <Box mt="2">
                        <img src={formik.values.thumbnail} alt="썸네일 미리보기" style={{ width: '200px' }} />
                      </Box>
                    )}
                  </Flex>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>

        {/* 아젠다 */}
        {Components.length > 0 && (
          <Suspense fallback={<div />}>
            {Components.map((Component, index) => (
              <Component key={index} index={index} formik={formik} setFormValues={setFormValues} readOnly />
            ))}
          </Suspense>
        )}
      </Box>

      <Box>
        <Flex gap="var(--space-2)" justify="end">
          {(type === 'admin' || type === 'master') && (
            <Button size="2" type="button" onClick={() => navigate(`/projectRequest/write/${id}`)}>
              수정
            </Button>
          )}
          <Button size="2" type="button" variant="outline" onClick={() => navigate('/projectRequest')}>
            목록
          </Button>
        </Flex>
      </Box>
    </Section>
  );
}
